import color from "color";
import { NavLink } from "react-router-dom";
import styled, { DefaultTheme } from "styled-components";

export const navWidth = "250px";
// hoverBg is now in the theme object — this is a compat alias for any non-styled-component usage
export const hoverBg = "#e1f0ff";

export const CloseNav = styled.div`
  display: none;
  position: absolute;
  right: 20px;
  top: 15px;

  ${({ theme }) => theme.sm`
    display: block;
  `}
`;

interface NavWrapperProps {
  $withMenu: boolean;
  $isOpen: boolean;
  onClick: (e: MouseEvent) => void;
}

export const NavWrapper = styled.div<NavWrapperProps>`
  height: 100%;
  width: ${navWidth};
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  font-weight: bold;
  color: ${({ theme }) => theme.black};
  transition: transform 0.2s ease-out, width 0.2s ease-out, min-width 0.2s ease-out;

  ${({ $withMenu }) =>
    !$withMenu &&
    `transform: translateX(-80px);
        width: 0 !important;
        min-width: 0 !important;
      `};

  ${({ theme, $isOpen }) =>
    theme.sm`
          position: fixed;
          transform: translateX(0);
          z-index: 20;
          width: 100%;
          
          ${
            !$isOpen &&
            `transform: translateX(-80px);
            width: 0;
            min-width: 0;
      
            ${CloseNav} {
              display: none;
            }
          `
          }
        `}
`;

// Below about this height the full nav (logo, links, chat bubble, footer;
// about 760px) no longer fits, so LinkLogoItem and ChatBubbleWrapper trim their
// whitespace. Past that, NavLinks scrolls.
const shortViewport = "@media (max-height: 800px)";

export const iconSize = (prop: string, theme: DefaultTheme) =>
  theme.range(prop, "22px", "22px");

export const LinkTitle = styled.span`
  font-size: 14px;
  width: 100%;
  text-decoration: none;
  font-weight: normal;
`;

const NavLinkBase = styled(NavLink)`
  position: relative;
  display: flex;
  width: calc(100% - 16px); // when we want menu items to have margin
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-size: 22px;
  color: ${({ theme }) => theme.navText};

  svg {
    fill: ${({ theme }) => color(theme.iconDefault).hex()};
    path {
      fill: ${({ theme }) => color(theme.iconDefault).hex()};
    }
    width: 100%;
    ${({ theme }) => iconSize("width", theme)}
    ${({ theme }) => iconSize("height", theme)}
    ${({ theme }) => iconSize("min-width", theme)}
    ${({ theme }) => theme.sm`
      margin-right: 10px;
    `}
  }

  &:hover {
    svg {
      fill: ${({ theme }) => color(theme.iconDefault).darken(0.2).hex()};
      path {
        fill: ${({ theme }) => color(theme.iconDefault).darken(0.2).hex()};
      }
    }

    ${LinkTitle} {
      //color: ${({ theme }) => color(theme.grey).darken(0.2).hex()};
    }
  }

  &.active ${LinkTitle} {
    color: ${({ theme }) => theme.activeText};
  }

  &.active svg {
    fill: ${({ theme }) => theme.activeText};

    path {
      fill: ${({ theme }) => theme.activeText};
    }
  }

  ${({ theme }) => theme.sm`
    margin: 5px 0;
  `}
`;

export const LinkItem = styled(NavLinkBase)`
  border-radius: 7px;
  margin: 2px 0;
  justify-content: space-between;
  padding: 14px 10px 14px 14px;

  &[hidden] {
    display: none;
  }

  ${({ theme }) => theme.sm`
    width: 100%;
    padding: 8px 16px;
  `}

  &:hover {
    background-color: ${({ theme }) => theme.subtleHover};
  }

  &.active {
    background-color: ${({ theme }) => theme.activeBg};
    box-shadow: ${({ theme }) => theme.activeShadow};
    border: ${({ theme }) => theme.activeBorder};

    ${LinkTitle} {
      font-weight: 500;
      color: ${({ theme }) => theme.activeText};
    }
  }

  ${LinkTitle} {
    margin-left: 6px;
  }
`;

export const LinkLogoItem = styled(NavLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 38px 15px 28px;
  ${({ theme }) => theme.range("margin", "20px", "30px")};

  ${shortViewport} {
    padding: 20px 15px 12px;
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;

export const LinksWrapper = styled.div`
  display: none;
  padding-left: 48px;

  ${LinkTitle} {
    font-size: 14px;
  }

  ${({ theme }) => theme.sm`
    display: block;
  `}
`;

// Positioning slot for ChatWidget inside the Nav structure. Lives between
// the menu links and the NavFooter, taking up remaining vertical space
// (it grows but never shrinks; NavLinks gives way instead) and aligning the
// bubble to its bottom. Inherits the nav's visibility — on mobile, the nav
// becomes an overlay (slide-out) and the chat bubble slides out with it. On public routes (no nav rendered),
// PublicRoute mounts its own ChatWidget at fixed position.
export const ChatBubbleWrapper = styled.div`
  flex: 1 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  padding-bottom: 50px;

  ${shortViewport} {
    padding-bottom: 16px;
  }
`;

// The logo and links, which scroll on their own when the viewport is too
// short for the whole nav, so the chat bubble and NavFooter below always stay
// on screen.
export const NavLinks = styled.div`
  flex: 0 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  ${({ theme }) => theme.sm`
    align-items: flex-start;
  `}
`;

export const NavFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  padding: 16px 8px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

export const Nav = styled.nav`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  border-right: ${({ theme }) => theme.navBorder};
  background-color: transparent;
  z-index: 1;
  width: ${navWidth};
  gap: 6px;

  ${({ theme }) => theme.sm`
      padding: 15px 10px;
      position: absolute;
      align-items: flex-start;
      z-index: 21;
      max-width: 300px;
      width: calc(100% - 80px);
      max-height: 100%;
      overflow-y: auto;
      background-color: ${color(theme.grey).lighten(0.2).hex()};

      & > ${LinkItem}:first-child {
        padding: 0;
        max-height: initial;
      }
    `}
`;
